import { Icon } from '@toggle/design-system';
import { Alert } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useConversationTask } from '~/react-query-hooks/alfred/useConversationTask';
import { getCardDate } from '~/utils/card-utils/card-utils';

import * as SharedStyles from '../../AlertsCard.styles';
import { extractContentPreview } from '../../utils/extract-content.preview';
import * as S from './AlertsAlfredCard.styles';

interface AlertsAlfredCardProps {
  alertEvent: Alert;
  dropdown?: React.ReactNode;
}

export const AlertsAlfredCard = ({
  alertEvent,
  dropdown,
}: AlertsAlfredCardProps) => {
  const { t } = useTranslation('alerts');
  const { created_at: createdAt, source_id: sourceId, read } = alertEvent;

  const { data: conversationData, isLoading } = useConversationTask(sourceId);
  const taskState = conversationData?.tasks?.[0]?.last_state;

  const isError =
    taskState?.status === 'Error' || taskState?.status === 'Fatal';

  if (isLoading) {
    return <SharedStyles.StyledLoader />;
  }

  if (!taskState) {
    return null;
  }

  return (
    <>
      <SharedStyles.Header>
        <SharedStyles.HeaderLeft>
          <span>{t('alerts:alfredCard.title')}</span>
        </SharedStyles.HeaderLeft>
        <SharedStyles.HeaderRight>
          <SharedStyles.DateSpan>
            {getCardDate(new Date(createdAt))}
          </SharedStyles.DateSpan>
          {dropdown}
        </SharedStyles.HeaderRight>
      </SharedStyles.Header>
      <SharedStyles.BodyWrapper>
        <SharedStyles.LogoWrapper>
          <S.AlfredLogo>
            <Icon iconName="Alfred" size={20} fillColor="var(--icon-primary)" />
          </S.AlfredLogo>
        </SharedStyles.LogoWrapper>
        <SharedStyles.TextContent>
          <SharedStyles.PrimaryText $read={read}>
            {conversationData?.name}
          </SharedStyles.PrimaryText>
          <S.SecondaryText>
            {isError ? (
              <S.ErrorWrapper>
                <Icon
                  fillColor={read ? 'var(--icon-soft)' : 'var(--icon-warning)'}
                  iconName="InformationCircle"
                  size={12}
                />
                <S.ErrorText $read={read}>
                  {t('alerts:alfredCard.errorText')}
                </S.ErrorText>
              </S.ErrorWrapper>
            ) : (
              extractContentPreview(taskState?.content ?? '')
            )}
          </S.SecondaryText>
        </SharedStyles.TextContent>
      </SharedStyles.BodyWrapper>
    </>
  );
};
